import {
    Box,
    Card,
    CardContent,
    Divider,
    Grid,
    Typography,
    useTheme
} from '@mui/material';
import dayjs from 'dayjs';

import DialogWrapper from '@libComponents/Dialog';
import { Button } from '@libComponents';
import ActionChip from './ActionChip';

const formatValue = (val) => {
    if (val === null || val === undefined) return '—';
    if (typeof val === 'object') return JSON.stringify(val, null, 2);
    return String(val);
};

const ActivityLogDetailsDialog = ({ open, onClose, log }) => {
    const theme = useTheme();

    if (!log) return null;

    const beforeState = log.changes?.before;
    const afterState = log.changes?.after;
    const hasChanges = beforeState !== undefined || afterState !== undefined;
    const hasMetadata = log.metadata && Object.keys(log.metadata).length > 0;

    // Collect all unique keys between before and after
    const changeKeys = Array.from(
        new Set([
            ...(beforeState && typeof beforeState === 'object' ? Object.keys(beforeState) : []),
            ...(afterState && typeof afterState === 'object' ? Object.keys(afterState) : [])
        ])
    );

    return (
        <DialogWrapper
            open={open}
            onClose={onClose}
            maxWidth='md'
            fullWidth
            dialogPadding={3}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {/* Header */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: 1
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Typography variant='h6' sx={{ fontWeight: 700 }}>
                            Activity Details #{log.id}
                        </Typography>
                        <ActionChip action={log.action} />
                    </Box>
                    <Typography variant='caption' color='text.secondary'>
                        {dayjs(log.createdAt).format('DD MMM YYYY, hh:mm:ss A')}
                    </Typography>
                </Box>

                <Divider />

                {/* Primary Meta Grid */}
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <Typography variant='caption' color='text.secondary' display='block'>
                            Actor Email
                        </Typography>
                        <Typography variant='body2' sx={{ fontWeight: 600 }}>
                            {log.actorEmail || 'System'}
                        </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Typography variant='caption' color='text.secondary' display='block'>
                            Target / Subject
                        </Typography>
                        <Typography variant='body2' sx={{ fontWeight: 600 }}>
                            {log.targetEmail || '—'}
                        </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Typography variant='caption' color='text.secondary' display='block'>
                            Module & Entity
                        </Typography>
                        <Typography variant='body2'>
                            <strong>{log.module}</strong> ({log.entity} {log.entityId ? `#${log.entityId}` : ''})
                        </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Typography variant='caption' color='text.secondary' display='block'>
                            IP Address & Client
                        </Typography>
                        <Typography variant='caption' sx={{ fontFamily: 'monospace' }}>
                            {log.ipAddress || '—'}
                        </Typography>
                    </Grid>

                    {log.description && (
                        <Grid item xs={12}>
                            <Typography variant='caption' color='text.secondary' display='block'>
                                Description
                            </Typography>
                            <Typography variant='body2' sx={{ mt: 0.5 }}>
                                {log.description}
                            </Typography>
                        </Grid>
                    )}
                </Grid>

                {/* State Changes Diff Section */}
                {hasChanges && (
                    <Box sx={{ mt: 1 }}>
                        <Typography variant='subtitle2' sx={{ fontWeight: 700, mb: 1 }}>
                            State Changes
                        </Typography>
                        {changeKeys.length > 0 ? (
                            <Card variant='outlined' sx={{ overflow: 'hidden' }}>
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: '25% 37.5% 37.5%',
                                        backgroundColor: theme.palette.mode === 'dark' ? '#262626' : '#F5F5F7',
                                        p: 1.5,
                                        fontWeight: 700,
                                        fontSize: '0.75rem',
                                        borderBottom: `1px solid ${theme.palette.divider}`
                                    }}
                                >
                                    <Box>Field</Box>
                                    <Box sx={{ color: 'error.main' }}>Previous State</Box>
                                    <Box sx={{ color: 'success.main' }}>New State</Box>
                                </Box>
                                {changeKeys.map((key) => {
                                    const beforeVal = beforeState ? beforeState[key] : undefined;
                                    const afterVal = afterState ? afterState[key] : undefined;
                                    return (
                                        <Box
                                            key={key}
                                            sx={{
                                                display: 'grid',
                                                gridTemplateColumns: '25% 37.5% 37.5%',
                                                p: 1.5,
                                                fontSize: '0.8125rem',
                                                borderBottom: `1px solid ${theme.palette.divider}`,
                                                '&:last-child': { borderBottom: 'none' }
                                            }}
                                        >
                                            <Typography variant='body2' sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
                                                {key}
                                            </Typography>
                                            <Typography
                                                variant='body2'
                                                sx={{
                                                    color: 'text.secondary',
                                                    fontFamily: typeof beforeVal === 'object' ? 'monospace' : 'inherit',
                                                    whiteSpace: 'pre-wrap',
                                                    wordBreak: 'break-word',
                                                    pr: 1
                                                }}
                                            >
                                                {formatValue(beforeVal)}
                                            </Typography>
                                            <Typography
                                                variant='body2'
                                                sx={{
                                                    fontWeight: 500,
                                                    fontFamily: typeof afterVal === 'object' ? 'monospace' : 'inherit',
                                                    whiteSpace: 'pre-wrap',
                                                    wordBreak: 'break-word',
                                                    pl: 1
                                                }}
                                            >
                                                {formatValue(afterVal)}
                                            </Typography>
                                        </Box>
                                    );
                                })}
                            </Card>
                        ) : (
                            <Card variant='outlined'>
                                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                                    <pre style={{ margin: 0, fontSize: '0.75rem', whiteSpace: 'pre-wrap' }}>
                                        {JSON.stringify(log.changes, null, 2)}
                                    </pre>
                                </CardContent>
                            </Card>
                        )}
                    </Box>
                )}

                {/* Metadata Section */}
                {hasMetadata && (
                    <Box sx={{ mt: 1 }}>
                        <Typography variant='subtitle2' sx={{ fontWeight: 700, mb: 0.5 }}>
                            Additional Metadata
                        </Typography>
                        <Card variant='outlined'>
                            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                                <pre style={{ margin: 0, fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                                    {JSON.stringify(log.metadata, null, 2)}
                                </pre>
                            </CardContent>
                        </Card>
                    </Box>
                )}

                {/* Footer */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                    <Button color='tertiary' onClick={onClose}>
                        Close
                    </Button>
                </Box>
            </Box>
        </DialogWrapper>
    );
};

export default ActivityLogDetailsDialog;
